import { useCallback, useEffect, useRef, useState } from "react";

import signalRecommenderTrackingService from "../services/tracking";
import type { DealSignalCsvValidation } from "../types/model";
import { validateDealSignalCsv } from "../utils/validateDealSignalCsv";

type DealSignalFileSelection = {
    readonly file?: File;
    readonly validation?: DealSignalCsvValidation;
    readonly isValidating: boolean;
    readonly selectFile: (file: File) => void;
};

export const useDealSignalFileSelection = (): DealSignalFileSelection => {
    const [file, setFile] = useState<File>();
    const [validation, setValidation] = useState<DealSignalCsvValidation>();
    const [isValidating, setIsValidating] = useState(false);

    /** Cancels the parse still running for a superseded pick, and marks its result as no longer wanted. */
    const parseAbortRef = useRef<AbortController | undefined>(undefined);

    useEffect(() => () => parseAbortRef.current?.abort(), []);

    const selectFile = useCallback((nextFile: File) => {
        parseAbortRef.current?.abort();

        const controller = new AbortController();

        parseAbortRef.current = controller;

        setFile(nextFile);
        setValidation(undefined);
        setIsValidating(true);

        void validateDealSignalCsv(nextFile, controller.signal).then((result) => {
            if (controller.signal.aborted) {
                return;
            }

            signalRecommenderTrackingService.trackFileValidated(result.isValid, result.companiesCount);

            setValidation(result);
            setIsValidating(false);
        });
    }, []);

    return { file, validation, isValidating, selectFile };
};
